import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, ListGroup, Row, Col, Spinner } from 'react-bootstrap';
import { useMenuData } from '../../hooks/useMenuData';
import { useUserPermissions, useVenueId } from '../../hooks/useUserData';
import MenuAndBillModal from './MenuAndBillModal';
import ConfirmActionModal from './ConfirmActionModal';
import { useVenueFeedback } from '../../context/VenueFeedbackContext';
import { canTransitionOrderStatus, getOrderTransitionErrorMessage } from '../../utils/orderStatusRules';
import { formatRelativeTime } from '../../utils/venueUiFormatters';
import { useAuth0 } from '@auth0/auth0-react';
import { patchVenueOrderItems, updateVenueOrderStatus } from '../../utils/staffApiService';
import history from '../../utils/history';
import '../../styles/TableOrdersModal.css';

const statusLabels = {
  pending: 'Bekliyor',
  preparing: 'Hazırlanıyor',
  completed: 'Tamamlandı',
};

const statusVariants = {
  pending: 'warning',
  preparing: 'primary',
  completed: 'success',
};

const STALE_MENU_ERROR_CODES = new Set(['ITEM_NOT_ORDERABLE', 'ITEM_UNAVAILABLE']);

const OrderDetailsModal = ({
  show,
  handleClose,
  order,
  isEditable,
  allowStatusActions = isEditable,
  showFooterActions = isEditable,
  onOrderStatusUpdated,
}) => {
  const venueId = useVenueId();
  const userPermissions = useUserPermissions() || [];
  const { getAccessTokenSilently } = useAuth0();
  const { pushFeedback } = useVenueFeedback();
  const menuData = useMenuData(venueId);
  const canAccessCashier = userPermissions.includes('take_payment');

  const [showMenuAndBillModal, setShowMenuAndBillModal] = useState(false);
  const [orderItems, setOrderItems] = useState(order?.items || []);
  const [currentStatus, setCurrentStatus] = useState(order?.status || '');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemPendingRemoval, setItemPendingRemoval] = useState(null);
  const [removingItemKey, setRemovingItemKey] = useState(null);

  useEffect(() => {
    if (order) {
      setOrderItems(order.items || []);
      setCurrentStatus(order.status);
    }
  }, [order]);

  const handleUpdateOrder = useCallback(async (nextItems) => {
    try {
      await patchVenueOrderItems(venueId, order.id, nextItems, getAccessTokenSilently);
      setOrderItems(nextItems);
      pushFeedback({
        type: 'success',
        title: 'Sipariş güncellendi',
        message: `Sipariş #${order.id} için ürün listesi yenilendi.`,
      });
      handleClose();
      return true;
    } catch (error) {
      if (!STALE_MENU_ERROR_CODES.has(error?.code)) {
        pushFeedback({
          type: 'error',
          title: 'Sipariş kaydedilemedi',
          message: error?.message || 'Sipariş düzenlenirken bir sorun oluştu.',
        });
      }
      throw error;
    }
  }, [getAccessTokenSilently, handleClose, order?.id, pushFeedback, venueId]);

  const handleSaveItemsFromModal = async (payload) => handleUpdateOrder(payload.items);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus || !order) return;
    if (!canTransitionOrderStatus(currentStatus, newStatus)) {
      pushFeedback({
        type: 'error',
        title: 'Geçersiz sipariş geçişi',
        message: getOrderTransitionErrorMessage(currentStatus, newStatus),
      });
      return;
    }

    setUpdatingStatus(true);
    try {
      await updateVenueOrderStatus(venueId, order.id, newStatus, getAccessTokenSilently);
      setCurrentStatus(newStatus);
      onOrderStatusUpdated?.(order.id, currentStatus, newStatus);
      pushFeedback({
        type: 'success',
        title: 'Sipariş durumu güncellendi',
        message: `Sipariş #${order.id} artık ${statusLabels[newStatus].toLowerCase()}.`,
      });
    } catch (error) {
      pushFeedback({
        type: 'error',
        title: 'Durum güncellenemedi',
        message: error?.message || 'Sipariş durumu güncellenemedi.',
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!order) return;

    try {
      await updateVenueOrderStatus(venueId, order.id, 'deleted', getAccessTokenSilently);
      pushFeedback({
        type: 'success',
        title: 'Sipariş silindi',
        message: `Sipariş #${order.id} listeden kaldırıldı.`,
      });
      setShowDeleteConfirm(false);
      handleClose();
    } catch (error) {
      pushFeedback({
        type: 'error',
        title: 'Sipariş silinemedi',
        message: error?.message || 'Sipariş silinirken bir hata oluştu.',
      });
    }
  };

  const handleRemoveItem = async () => {
    if (!order || !itemPendingRemoval) return;

    const itemKey = itemPendingRemoval.orderItemId || itemPendingRemoval.id;
    setRemovingItemKey(itemKey);

    try {
      await patchVenueOrderItems(
        venueId,
        order.id,
        [
          {
            id: itemPendingRemoval.id,
            changeType: 'remove',
            quantity: itemPendingRemoval.quantity,
          },
        ],
        getAccessTokenSilently
      );

      setOrderItems((currentItems) =>
        currentItems.filter((item) => {
          if (itemPendingRemoval.orderItemId && item.orderItemId) {
            return item.orderItemId !== itemPendingRemoval.orderItemId;
          }
          return item.id !== itemPendingRemoval.id;
        })
      );
      setItemPendingRemoval(null);
      pushFeedback({
        type: 'success',
        title: 'Ürün kaldırıldı',
        message: `${itemPendingRemoval.name} siparişten çıkarıldı.`,
      });
    } catch (error) {
      pushFeedback({
        type: 'error',
        title: 'Ürün kaldırılamadı',
        message: error?.message || 'Ürün siparişten çıkarılırken bir hata oluştu.',
      });
    } finally {
      setRemovingItemKey(null);
    }
  };

  const handleOpenCashier = () => {
    const tableId = order?.table_id ?? order?.tableId;
    if (!tableId) return;
    handleClose();
    history.push(`/cashier?tableId=${tableId}`);
  };

  if (!order) return null;

  const canRemoveLineItems = isEditable && orderItems.length > 1;

  return (
    <>
      <Modal show={show} onHide={handleClose} centered dialogClassName="venue-order-details-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="table-orders-modal__title">Sipariş #{order.id}</div>
            <div className="table-orders-modal__subtitle">
              {formatRelativeTime(order.created_at)} • {order?.tables?.name || 'Masa bilgisi bekleniyor'}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="venue-modal-summary">
            <div className="venue-modal-summary__item">
              <span className="venue-modal-summary__label">Durum</span>
              <span className="venue-modal-summary__value">{statusLabels[currentStatus]}</span>
            </div>
            <div className="venue-modal-summary__item">
              <span className="venue-modal-summary__label">Ürün</span>
              <span className="venue-modal-summary__value">{orderItems.length}</span>
            </div>
          </div>

          {order.notes ? (
            <div className="venue-flow-alert venue-flow-alert--info">
              <strong>Sipariş notu</strong>
              {'\n'}
              {order.notes}
            </div>
          ) : null}

          {allowStatusActions ? (
            <div className="order-details-status-bar">
              {['pending', 'preparing', 'completed'].map((status) => (
                <Button
                  key={status}
                  className="order-details-status-bar__button"
                  variant={status === currentStatus ? statusVariants[status] : 'outline-secondary'}
                  disabled={
                    updatingStatus ||
                    status === currentStatus ||
                    !canTransitionOrderStatus(currentStatus, status)
                  }
                  onClick={() => handleStatusChange(status)}
                >
                  {updatingStatus && currentStatus !== status ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : null}
                  {statusLabels[status]}
                </Button>
              ))}
            </div>
          ) : null}

          <ListGroup className="order-details-list">
            {orderItems.map((item, index) => (
              <ListGroup.Item key={item.orderItemId || `${item.id}-${index}`}>
                <Row>
                  <Col>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div>{item.quantity} x {item.name}</div>
                        {item.notes ? (
                          <div className="small text-muted mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                            <strong className="d-block mb-1">Ürün notu</strong>
                            <span>{item.notes}</span>
                          </div>
                        ) : null}
                      </div>
                      {canRemoveLineItems ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={Boolean(removingItemKey)}
                          onClick={() => setItemPendingRemoval(item)}
                        >
                          {removingItemKey === (item.orderItemId || item.id) ? 'Kaldırılıyor...' : 'Ürünü Kaldır'}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>

        {showFooterActions ? (
          <Modal.Footer>
            <div className="venue-modal-actions">
              <Button variant="outline-primary" onClick={() => setShowMenuAndBillModal(true)}>
                Düzenle
              </Button>
              {canAccessCashier && (order?.table_id || order?.tableId) ? (
                <Button variant="outline-success" onClick={handleOpenCashier}>
                  Kasaya Git
                </Button>
              ) : null}
              <div className="venue-modal-actions__spacer" />
              <Button variant="outline-danger" onClick={() => setShowDeleteConfirm(true)}>
                Tüm Siparişi Sil
              </Button>
            </div>
          </Modal.Footer>
        ) : null}
      </Modal>

      {isEditable && menuData ? (
        <MenuAndBillModal
          show={showMenuAndBillModal}
          handleClose={() => setShowMenuAndBillModal(false)}
          menu={menuData}
          tableSummary={orderItems}
          saveOrder={handleSaveItemsFromModal}
          contextType={'EditOrder'}
        />
      ) : null}

      <ConfirmActionModal
        show={Boolean(itemPendingRemoval)}
        title="Ürünü kaldır"
        message="Bu ürünü siparişten kaldırmak istediğinize emin misiniz?"
        confirmLabel="Ürünü Kaldır"
        cancelLabel="Vazgeç"
        confirmVariant="danger"
        onConfirm={handleRemoveItem}
        onClose={() => setItemPendingRemoval(null)}
      />

      <ConfirmActionModal
        show={showDeleteConfirm}
        title="Siparişi sil"
        message="Bu siparişi silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmLabel="Siparişi Sil"
        cancelLabel="Vazgeç"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </>
  );
};

export default OrderDetailsModal;
